'use client'

import { format, parseISO } from 'date-fns'
import { sk } from 'date-fns/locale'
import React, { useEffect } from 'react'

import { cn } from '@/_lib/utils'

import { useCalendarCore } from './a-product-template-calendar-core'
import {
	AProductTemplateCalendarLoading,
	calendarSlotsAreaInlineRowClass
} from './a-product-template-calendar-loading'
import { templateCalendarQuickSlotButtonClass } from './a-product-template-calendar-slot-button.class'
import { AProductTemplateCalendarWeekNav } from './a-product-template-calendar-week-nav'

import type { CalendarTimeslot, DateSlot } from './a-product-template-calendar-types'

/**
 * Mobile calendar: week navigation, weekday strip, and slot buttons for the selected day.
 *
 * @remarks When `slots` updates, the selected day jumps to the earliest slot date in the payload.
 */
interface AProductTemplateCalendarMobileProps {
	slots?: DateSlot[]
	isSlotsLoading?: boolean
	onSlotSelect?: (slot: CalendarTimeslot) => void
	startHour?: number
	endHour?: number
	dateFrom?: string
	dateTo?: string
	onWeekChange?: (dateFrom: string, dateTo: string) => void
	/** Wider slot grid (2/4/6/8 cols) and time-only labels, used when every slot is under 10 minutes. */
	denseSlots?: boolean
}

/**
 * Slovak short weekday (`EEE`) with the first letter uppercased for UI consistency.
 *
 * @param date - Day used only for formatting (locale `sk`).
 */
function formatSkWeekdayShort(date: Date): string {
	const raw = format(date, 'EEE', { locale: sk })
	return raw.replace(/^./u, ch => ch.toLocaleUpperCase('sk'))
}

/** @see {@link AProductTemplateCalendarMobileProps} */
const AProductTemplateCalendarMobile: React.FC<AProductTemplateCalendarMobileProps> = ({
	slots = [],
	isSlotsLoading = false,
	onSlotSelect,
	startHour = 7,
	endHour = 20,
	dateFrom,
	dateTo,
	onWeekChange,
	denseSlots = false
}) => {
	const {
		weekDays,
		selectedDayIndex,
		setSelectedDayIndex,
		goToPrevWeek,
		goToNextWeek,
		weekRange,
		daySlots,
		getDaySlots,
		convertSlotToTimeslot
	} = useCalendarCore(slots, startHour, endHour, dateFrom, dateTo, onWeekChange)

	useEffect(() => {
		if (slots.length > 0) {
			let closestSlot: DateSlot | null = null

			for (const slot of slots) {
				if (!closestSlot || new Date(slot.date) < new Date(closestSlot.date)) {
					closestSlot = slot
				}
			}

			const closestSlotIndex = weekDays.findIndex(day => day.fullDate === closestSlot?.date)

			if (closestSlotIndex !== -1) {
				setSelectedDayIndex(closestSlotIndex)
			}
		}
	}, [slots, weekDays, setSelectedDayIndex])

	return (
		<div className='bg-transparent p-5'>
			<AProductTemplateCalendarWeekNav
				className='mb-5'
				weekRange={weekRange}
				onPrevWeek={goToPrevWeek}
				onNextWeek={goToNextWeek}
			/>
			<div
				className='mb-4 grid w-full gap-2'
				style={{ gridTemplateColumns: `repeat(${weekDays.length}, minmax(0, 1fr))` }}
			>
				{weekDays.map((day, idx) => {
					const dayHasSlots = getDaySlots(day.fullDate).length > 0
					const isEmptyDay = !isSlotsLoading && !dayHasSlots

					return (
						<button
							key={idx}
							type='button'
							className={cn(
								'flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-2xl px-1 py-2 text-center transition-colors',
								isEmptyDay && 'relative overflow-hidden opacity-50',
								selectedDayIndex === idx
									? 'bg-medante-purple-dark text-white shadow-sm'
									: 'bg-medante-purple-dark/8 text-medante-purple-dark ring-1 ring-medante-purple-dark/15 hover:bg-medante-purple-dark/12'
							)}
							onClick={() => setSelectedDayIndex(idx)}
						>
							<span
								className={cn(
									'text-xs font-semibold leading-none tracking-tight',
									selectedDayIndex === idx ? 'text-white' : 'text-medante-purple-dark'
								)}
							>
								{formatSkWeekdayShort(day.date)}
							</span>
							<span
								className={cn(
									'text-xs leading-none font-medium',
									selectedDayIndex === idx ? 'text-white/90' : 'text-medante-purple-dark/75'
								)}
							>
								{format(day.date, 'd.M.')}
							</span>
							{isEmptyDay ? (
								<span
									aria-hidden
									className='pointer-events-none absolute left-1/2 top-1/2 z-1 h-px w-[170%] -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-current opacity-70'
								/>
							) : null}
						</button>
					)
				})}
			</div>
			<div>
				{isSlotsLoading ? (
					<AProductTemplateCalendarLoading />
				) : daySlots.length === 0 ? (
					<div className={calendarSlotsAreaInlineRowClass}>
						V tento deň nie sú žiadne voľné termíny
					</div>
				) : (
					<div
						className={cn(
							'grid gap-2',
							denseSlots
								? 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8'
								: 'grid-cols-2'
						)}
					>
						{daySlots.map((slot, i) => (
							<button
								key={i}
								type='button'
								className={cn(
									templateCalendarQuickSlotButtonClass,
									'min-w-0 w-full justify-center'
								)}
								onClick={() => onSlotSelect?.(convertSlotToTimeslot(slot))}
							>
								{denseSlots ? (
									slot.startTime
								) : (
									<>
										{format(parseISO(slot.date), 'dd.MM.yyyy')}
										<div
											className='h-0.5 w-0.5 shrink-0 rounded-full bg-white'
											aria-hidden
										/>
										{slot.startTime}
									</>
								)}
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	)
}

export default AProductTemplateCalendarMobile
